import { storageService } from './storage';
import { PolarstepsTrip, PolarstepsLocationPoint } from '../types/polarsteps';
import {
  safeParseLocationsJson,
  safeParseTripJson,
  fusePolarstepsData,
} from './polarstepsParser';
import { interpolateGpsLocation } from './gpsInterpolator';

export interface ManagedTripOption {
  id: string | number;
  name: string;
  photoCount: number;
  hasPolarsteps: boolean;
  locationCount: number;
  stepCount: number;
  isComingSoon?: boolean;
}

export interface AttachTraceResult {
  success: boolean;
  trip?: PolarstepsTrip;
  locationCount: number;
  stepCount: number;
  photosReGeotagged: number;
  totalPhotos: number;
  message: string;
  error?: string;
}

/**
 * Returns available trips with photo and Polarsteps status
 */
export async function getManagedTrips(): Promise<ManagedTripOption[]> {
  if (typeof indexedDB === 'undefined') {
    return [
      {
        id: 'islanda-trip',
        name: 'La nostra Islanda',
        photoCount: 0,
        hasPolarsteps: false,
        locationCount: 0,
        stepCount: 0,
      },
      {
        id: 'coming-soon-giappone',
        name: 'Giappone',
        photoCount: 0,
        hasPolarsteps: false,
        locationCount: 0,
        stepCount: 0,
        isComingSoon: true,
      },
    ];
  }

  try {
    const storedTrips = await storageService.getAllTrips();
    const storedPhotos = await storageService.getPhotos();

    const options: ManagedTripOption[] = [];

    // 1. Process stored trips from DB
    for (const trip of storedTrips) {
      const tripPhotos = storedPhotos.filter((p) => {
        if (p.tripId) return p.tripId === trip.id;
        // Legacy fallback
        return (
          trip.id === 'islanda-trip' ||
          trip.id === 'stored-active-trip' ||
          String(trip.id).includes('islanda')
        );
      });

      let displayName = trip.name;
      if (
        !displayName ||
        displayName.toLowerCase().includes('polarsteps') ||
        displayName.toLowerCase().includes('madeira') ||
        displayName === 'Traccia Polarsteps' ||
        displayName === 'Viaggio Polarsteps'
      ) {
        displayName = 'La nostra Islanda';
      }

      options.push({
        id: trip.id,
        name: displayName,
        photoCount: tripPhotos.length,
        hasPolarsteps: (trip.locations?.length || 0) > 0,
        locationCount: trip.locations?.length || 0,
        stepCount: trip.all_steps?.length || 0,
      });
    }

    // 2. Ensure "La nostra Islanda" exists as an option if not already present
    const hasIceland = options.some(
      (o) => o.id === 'islanda-trip' || o.name.toLowerCase().includes('islanda')
    );
    if (!hasIceland) {
      options.unshift({
        id: 'islanda-trip',
        name: 'La nostra Islanda',
        photoCount: storedPhotos.length,
        hasPolarsteps: false,
        locationCount: 0,
        stepCount: 0,
      });
    }

    // 3. Add Giappone
    const hasGiappone = options.some(
      (o) => o.id === 'coming-soon-giappone' || o.name.toLowerCase().includes('giappone')
    );
    if (!hasGiappone) {
      options.push({
        id: 'coming-soon-giappone',
        name: 'Giappone',
        photoCount: 0,
        hasPolarsteps: false,
        locationCount: 0,
        stepCount: 0,
        isComingSoon: true,
      });
    }

    return options;
  } catch (err) {
    console.warn('[tripManager] Errore lettura viaggi:', err);
    return [
      {
        id: 'islanda-trip',
        name: 'La nostra Islanda',
        photoCount: 0,
        hasPolarsteps: false,
        locationCount: 0,
        stepCount: 0,
      },
      {
        id: 'coming-soon-giappone',
        name: 'Giappone',
        photoCount: 0,
        hasPolarsteps: false,
        locationCount: 0,
        stepCount: 0,
        isComingSoon: true,
      },
    ];
  }
}

/**
 * Attaches a Polarsteps JSON trace (locations and/or trip) to a designated trip.
 * Persists the updated trip into IndexedDB and immediately re-correlates GPS coordinates
 * for all photos belonging to that trip.
 */
export async function attachPolarstepsTrace(
  tripId: string | number,
  tripName: string,
  files: { locationsFile?: File; tripFile?: File }
): Promise<AttachTraceResult> {
  if (!files.locationsFile && !files.tripFile) {
    return {
      success: false,
      locationCount: 0,
      stepCount: 0,
      photosReGeotagged: 0,
      totalPhotos: 0,
      message: 'Nessun file Polarsteps fornito.',
      error: 'File mancante',
    };
  }

  try {
    let locationsResult;
    let tripResult;

    if (files.locationsFile) {
      const locText = await files.locationsFile.text();
      locationsResult = safeParseLocationsJson(locText);
      const tripCandidate = safeParseTripJson(locText);
      if (tripCandidate.success && tripCandidate.data) {
        tripResult = tripCandidate;
      }
    }

    if (files.tripFile && files.tripFile !== files.locationsFile) {
      const tripText = await files.tripFile.text();
      const parsedTrip = safeParseTripJson(tripText);
      if (parsedTrip.success && parsedTrip.data) {
        tripResult = parsedTrip;
      }
      if (!locationsResult || !locationsResult.success || (locationsResult.data?.length ?? 0) === 0) {
        const locCandidate = safeParseLocationsJson(tripText);
        if (locCandidate.success && (locCandidate.data?.length ?? 0) > 0) {
          locationsResult = locCandidate;
        }
      }
    }

    const fusion = fusePolarstepsData(locationsResult, tripResult);

    const locations: PolarstepsLocationPoint[] = fusion.data?.locations || [];
    const allSteps = fusion.data?.all_steps || [];
    const timezoneId = fusion.data?.timezone_id || 'Atlantic/Reykjavik';

    // Retrieve or create trip record
    const allStored = await storageService.getAllTrips();
    let trip = allStored.find((t) => t.id === tripId || t.name === tripName);

    if (!trip) {
      trip = {
        id: tripId,
        name: tripName,
        start_date: fusion.data?.start_date || Math.floor(Date.now() / 1000),
        end_date: fusion.data?.end_date || Math.floor(Date.now() / 1000),
        timezone_id: timezoneId,
        all_steps: allSteps,
        locations: locations,
      };
    } else {
      trip = {
        ...trip,
        name: tripName,
        locations: locations.length > 0 ? locations : trip.locations,
        all_steps: allSteps.length > 0 ? allSteps : trip.all_steps,
        timezone_id: fusion.data?.timezone_id || trip.timezone_id || timezoneId,
        start_date: fusion.data?.start_date || trip.start_date,
        end_date: fusion.data?.end_date || trip.end_date,
      };
    }

    // Persist trip record in IndexedDB
    await storageService.saveTrip(trip);

    // Re-correlate existing photos for this trip
    const storedPhotos = await storageService.getPhotos();
    const photosForTrip = storedPhotos.filter((p) => {
      if (p.tripId) return p.tripId === tripId;
      return (
        tripId === 'islanda-trip' ||
        tripId === 'stored-active-trip' ||
        String(tripId).includes('islanda')
      );
    });

    let reGeotagged = 0;
    if (trip.locations.length > 0 && photosForTrip.length > 0) {
      for (const p of photosForTrip) {
        const match = interpolateGpsLocation(p.timestampUtc, trip.locations, trip.all_steps, {
          fallbackToNearest: true,
        });

        if (match.lat !== null && match.lon !== null) {
          p.lat = match.lat;
          p.lon = match.lon;
          p.matchingConfidence = match.matchingConfidence;
          p.stepName = match.stepName;
          p.tripId = tripId;
          reGeotagged++;
        }
      }

      await storageService.updatePhotoMetadataBatch(photosForTrip);
    }

    return {
      success: true,
      trip,
      locationCount: trip.locations.length,
      stepCount: trip.all_steps.length,
      photosReGeotagged: reGeotagged,
      totalPhotos: photosForTrip.length,
      message: `Traccia salvata con successo: ${trip.locations.length} punti GPS e ${trip.all_steps.length} tappe. ${reGeotagged} foto ri-geolocalizzate su ${photosForTrip.length}.`,
    };
  } catch (err: unknown) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    console.error('[tripManager] Errore associazione traccia Polarsteps:', err);
    return {
      success: false,
      locationCount: 0,
      stepCount: 0,
      photosReGeotagged: 0,
      totalPhotos: 0,
      message: `Errore durante il parsing o il salvataggio della traccia: ${errorMsg}`,
      error: errorMsg,
    };
  }
}
